import styles from "./CartProductCard.module.css"
import React from 'react';
import { X, Minus, Plus, PencilLine } from 'lucide-react';
import { useCartContext } from "../../../context/CartContext";
import { useDetailContext } from '../../../context/DetailContext';
import useTimeModalActions from "../../../hooks/useTimeModalActions";
import { usePreferences } from "../../../context/PreferencesContext";
import { czk_eur_rate } from "../../../constants/prices";
import { formatCurrencyFromCzk } from "../../../utils/prices";
import { useTranslation } from "react-i18next";
import { CartVenueProps } from "../../../types/venue.types";
import { normalizeDate } from "../../../utils/calendar";
import { cartChange } from "../../../utils/cart";
import { usePublicCatalog } from "../../../context/PublicCatalogContext";
/**
* Cart card for venues
*
* @param props CartVenueProps
* @param {Venue} props.product - Product
* @param {string} props.time - Delivery time
* @param {string} props.date - Delivery date
* @returns TSX.Element
*/
const CartVenueCard = ({ venue, time, date }: CartVenueProps) => {
    const { cartData, reloadCart } = useCartContext();
    const { t } = useTranslation(); // Translation
    const {isPublicView} = usePublicCatalog();
    const { openDetail } = useDetailContext(); // Detail context
    const { openTimeModal } = useTimeModalActions(); // Time modal toggle 
    const { selectedCurrency } = usePreferences(); // Curent selected currency
    const itemsForDate = cartData?.items?.[date] ?? [];
    const serverItem = itemsForDate.find(
        (cartProduct) =>
            cartProduct.id === venue.id &&
            cartProduct.time === time &&
            cartProduct.category === "venue"
    );
    const cartAmount = serverItem?.amount ?? 0; // Amount in cart
    const showAmountControls = cartAmount > 0 && !isPublicView;
    const linePriceWithoutTax =
        typeof serverItem?.price_per_piece === "number"
            ? serverItem.price_per_piece * cartAmount
            : venue.price != null
                ? venue.price * cartAmount
                : (typeof serverItem?.price_without_tax === "number" ? serverItem.price_without_tax : null);
    const noDphText =
        linePriceWithoutTax != null
            ? formatCurrencyFromCzk(linePriceWithoutTax, czk_eur_rate, selectedCurrency)
            : "N/A"; // Total price
    const handleOpen = () => {
        openDetail && openDetail({id: venue.id, type: "venue" });
    }

    return(
        <>
        <div className={styles.CartProduct} key={venue.id} onClick={handleOpen}>
            <div className={styles.CartProductImage} style={{ backgroundImage: `url(${venue.photo})` }}></div>
            <div className={styles.CartProductContent}>
                <div className={styles.CartProductInfo}>
                    <p className={styles.CartProductTitle}>{venue.title}</p>
                    <div className={styles.CartProductButtons} onClick={(e) => e.stopPropagation()}>
                        <button
                            className={styles.CartProductButton}
                            onClick={() =>
                                openTimeModal(
                                    {
                                        type: "single",
                                        date: normalizeDate(date),
                                        time: time,
                                        productId: venue.id,
                                        prodType: "venue",
                                    },
                                    { openOnTop: true }
                                )
                            }
                        >
                            <PencilLine/>
                        </button>
                        {showAmountControls && (
                            <button className={styles.CartProductButton} onClick={()=>cartChange(cartData, venue.id, normalizeDate(date), "", 0, "venue", undefined, undefined, reloadCart)} >
                                <X/>
                            </button>
                        )}
                    </div>
                </div>
                    <div className={styles.CartProductOrderInfo}>

                        {showAmountControls && (
                            <div className={styles.CartProductAmount} onClick={(e) => e.stopPropagation()}>
                                <button disabled={(cartAmount==1 && true) || isPublicView} className={cartAmount == 1 ? `${styles.CartProductAmountDecrease} ${styles.CartProductAmountDisabled}` : `${styles.CartProductAmountDecrease}`} onClick={()=>cartChange(cartData, venue.id, normalizeDate(date), time, cartAmount-1, "venue", normalizeDate(date), time, reloadCart)}>
                                    <Minus/>
                                </button>
                                <div className={styles.CartProductAmountText}>
                                    <p>{cartAmount}</p>
                                </div>
                                <button className={styles.CartProductAmountIncrease} onClick={()=>cartChange(cartData, venue.id, normalizeDate(date), time, cartAmount+1, "venue", normalizeDate(date), time, reloadCart)} disabled={isPublicView}>
                                    <Plus/>
                                </button>
                            </div>
                        )}
                        <p className={styles.CartProductPrice}> {t("products.no_dph_short")} {noDphText}</p>
                </div>
            </div>
        </div>
        </>
    )
}   

export default CartVenueCard
